import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, ArrowRight, ArrowLeft, ShieldCheck, LogOut, TrendingUp, Save, Trash2 } from "lucide-react";
import axios from "axios";
import { LogoMark, Wordmark } from "@/components/Logo";
import { API_BASE, apiErrorMessage } from "@/lib/api";

const TOKEN_KEY = "brvm_calc_token";

// Separate client: the shared api instance forces the marketplace token onto
// every request (and clears it on 401) — calculator calls must carry ONLY the
// scoped calc token.
const calcApi = axios.create({ baseURL: API_BASE });

// Where the main marketplace lives, for the gate's sign-in link. When the app
// is served from the calculator subdomain, the main site is the bare domain.
function mainSiteUrl(path) {
  const h = window.location.hostname;
  if (h.startsWith("profitcalculator.")) return `https://${h.replace("profitcalculator.", "")}${path}`;
  return path;
}

export function CalcCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const started = useRef(false); // StrictMode double-fires effects in dev — redeem exactly once

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const ticket = params.get("ticket") || "";
      if (!ticket) {
        setError("Missing sign-in ticket.");
        return;
      }
      try {
        const { data } = await calcApi.post("/sso/redeem", { ticket });
        localStorage.setItem(TOKEN_KEY, data.calc_token);
        // forward haul prefill params carried on the ticket link
        const qs = new URLSearchParams();
        if (params.get("miles")) qs.set("miles", params.get("miles"));
        if (params.get("payout")) qs.set("payout", params.get("payout"));
        const tail = qs.toString();
        nav(`/profit-calculator${tail ? `?${tail}` : ""}`, { replace: true });
      } catch (err) {
        setError(apiErrorMessage(err, "That sign-in link has expired or was already used."));
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CalcShell>
      <div className="text-center py-24" data-testid="calc-callback">
        {error ? (
          <>
            <h1 className="font-display text-3xl font-black uppercase">Couldn't sign you in</h1>
            <p className="text-zinc-500 text-sm mt-3 max-w-sm mx-auto" data-testid="calc-callback-error">{error}</p>
            <GateActions />
          </>
        ) : (
          <p className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Signing you in securely…</p>
        )}
      </div>
    </CalcShell>
  );
}

export function ProfitCalculator() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [driver, setDriver] = useState(null);
  const [denied, setDenied] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fuelNote, setFuelNote] = useState("");
  const [f, setF] = useState({
    // haul links prefill miles/payout: /profit-calculator?miles=…&payout=…
    miles: params.get("miles") || "",
    payout: params.get("payout") || "",
    mpg: "", fuel_price: "", days: "", daily_cost: "", tolls: "", other: "",
  });

  const calcHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setDenied(true);
        return;
      }
      try {
        const { data } = await calcApi.get("/sso/calc/me", { headers: calcHeaders() });
        setDriver(data.driver);
        const list = await calcApi.get("/sso/calc/calculations", { headers: calcHeaders() });
        setSaved(list.data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setDenied(true);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const autofillFuel = async () => {
    try {
      const { data } = await calcApi.get("/sso/calc/fuel-price", { headers: calcHeaders() });
      setF((s) => ({ ...s, fuel_price: String(data.price_usd) }));
      setFuelNote(`EIA U.S. diesel avg · week of ${data.period}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't fetch the diesel price — enter it manually."));
    }
  };

  const saveRun = async () => {
    if (saving || !(parseFloat(f.payout) > 0)) {
      if (!(parseFloat(f.payout) > 0)) toast.error("Enter a payout first.");
      return;
    }
    setSaving(true);
    try {
      const label = `${f.miles || "?"} mi · $${f.payout} payout`;
      const { data } = await calcApi.post(
        "/sso/calc/calculations",
        { label, inputs: f, results: { net: r.net, per_mile: r.perMile, total_cost: r.total } },
        { headers: calcHeaders() }
      );
      setSaved((s) => [data, ...s].slice(0, 50));
      toast.success("Calculation saved.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteRun = async (id) => {
    try {
      await calcApi.delete(`/sso/calc/calculations/${id}`, { headers: calcHeaders() });
      setSaved((s) => s.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const r = useMemo(() => {
    const n = (v) => parseFloat(v) || 0;
    const miles = n(f.miles), mpg = n(f.mpg), price = n(f.fuel_price);
    const days = n(f.days), daily = n(f.daily_cost), tolls = n(f.tolls), other = n(f.other), payout = n(f.payout);
    const fuel = mpg > 0 ? (miles / mpg) * price : 0;
    const road = days * daily + tolls + other;
    const total = fuel + road;
    const net = payout - total;
    const perMile = miles > 0 ? payout / miles : 0;
    const margin = payout > 0 ? net / payout : 0;
    let verdict = "Enter your numbers";
    if (payout > 0 && miles > 0) {
      if (net < 0) verdict = "Losing money — pass or renegotiate";
      else if (perMile < 1.5) verdict = "Thin margin";
      else if (perMile < 2.5) verdict = "Fair money";
      else verdict = "Strong haul";
    }
    return { fuel, road, total, net, perMile, margin, verdict };
  }, [f]);

  const money = (v) => v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    toast.success("Signed out of the calculator.");
    nav(0);
  };

  if (denied) {
    return (
      <CalcShell>
        <div className="text-center py-24" data-testid="calc-gate">
          <ShieldCheck className="w-10 h-10 text-[#FF3B30] mx-auto mb-5" />
          <h1 className="font-display text-3xl font-black uppercase">Registered drivers only</h1>
          <p className="text-zinc-500 text-sm mt-3 max-w-md mx-auto leading-relaxed">
            The profit calculator signs you in automatically from your Boat RV Moving driver passport — no second password, no exceptions.
          </p>
          <GateActions />
        </div>
      </CalcShell>
    );
  }

  if (!driver) {
    return (
      <CalcShell>
        <p className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse py-24 text-center">Loading…</p>
      </CalcShell>
    );
  }

  return (
    <CalcShell driver={driver} onSignOut={signOut}>
      <div className="max-w-6xl mx-auto" data-testid="calc-page">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Driver tools</span>
        <h1 className="font-display text-4xl md:text-6xl font-black uppercase mt-2 leading-none">Profit calculator</h1>
        <p className="text-sm text-zinc-500 mt-3 max-w-xl">
          Know your number before you bid. Fuel, days on the road, tolls — in; profit per haul and per mile — out.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Total trip miles" value={f.miles} onChange={set("miles")} testid="calc-miles" hint="Loaded + deadhead" />
            <Field label="Truck MPG" value={f.mpg} onChange={set("mpg")} testid="calc-mpg" hint="Loaded, not highway spec" />
            <div>
              <Field label="Fuel price $/gal" value={f.fuel_price} onChange={set("fuel_price")} testid="calc-fuel-price" />
              <button
                type="button"
                onClick={autofillFuel}
                data-testid="calc-fuel-autofill"
                className="mt-1.5 text-[10px] uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white transition-colors"
              >
                Use current US diesel avg
              </button>
              {fuelNote && <div className="text-[10px] text-zinc-600 mt-1" data-testid="calc-fuel-note">{fuelNote}</div>}
            </div>
            <Field label="Days on the road" value={f.days} onChange={set("days")} testid="calc-days" />
            <Field label="Cost per day ($)" value={f.daily_cost} onChange={set("daily_cost")} testid="calc-daily-cost" hint="Food, lodging, parking" />
            <Field label="Tolls ($)" value={f.tolls} onChange={set("tolls")} testid="calc-tolls" />
            <Field label="Other costs ($)" value={f.other} onChange={set("other")} testid="calc-other" hint="Permits, escorts, washout…" />
            <Field label="Offered payout ($)" value={f.payout} onChange={set("payout")} testid="calc-payout" accent />
          </div>

          <div className="border border-white/[0.08] bg-[#0F0F10] p-6 h-fit lg:sticky lg:top-8" data-testid="calc-results">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Net profit</div>
            <div
              className={`font-display text-5xl font-black mt-2 leading-none ${r.net >= 0 ? "text-emerald-400" : "text-red-400"}`}
              data-testid="calc-net"
            >
              {money(r.net)}
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-1 border border-white/10 text-zinc-300" data-testid="calc-verdict">
              <TrendingUp className="w-3 h-3 text-[#FF3B30]" /> {r.verdict}
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <Row label="Fuel" value={money(r.fuel)} testid="calc-out-fuel" />
              <Row label="Road costs" value={money(r.road)} testid="calc-out-road" />
              <Row label="Total cost" value={money(r.total)} testid="calc-out-total" strong />
              <div className="border-t border-white/[0.08] pt-3">
                <Row label="Payout per mile" value={r.perMile ? `$${r.perMile.toFixed(2)}` : "—"} testid="calc-per-mile" />
                <Row label="Margin" value={`${(r.margin * 100).toFixed(0)}%`} testid="calc-margin" />
              </div>
            </div>

            <button
              onClick={saveRun}
              disabled={saving}
              data-testid="calc-save-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold transition-colors disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save calculation"}
            </button>
          </div>

          {saved.length > 0 && (
            <div className="border border-white/[0.08] bg-[#0F0F10] lg:col-start-2 h-fit" data-testid="calc-saved-list">
              <div className="p-4 border-b border-white/[0.06] text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
                Saved runs
              </div>
              {saved.map((s) => (
                <div key={s.id} className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.04] last:border-0" data-testid={`calc-saved-${s.id}`}>
                  <button
                    onClick={() => setF({ miles: "", payout: "", mpg: "", fuel_price: "", days: "", daily_cost: "", tolls: "", other: "", ...s.inputs })}
                    data-testid={`calc-load-${s.id}`}
                    className="flex-1 text-left group"
                  >
                    <div className="text-sm font-bold truncate group-hover:text-[#FF3B30] transition-colors">{s.label}</div>
                    <div className="text-[10px] text-zinc-500">
                      net <span className={(s.results?.net ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}>${Math.round(s.results?.net || 0).toLocaleString()}</span>
                      {" · "}{new Date(s.created_at).toLocaleDateString()}
                    </div>
                  </button>
                  <button
                    onClick={() => deleteRun(s.id)}
                    data-testid={`calc-del-${s.id}`}
                    aria-label="Delete saved calculation"
                    className="text-zinc-600 hover:text-red-400 transition-colors p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CalcShell>
  );
}

function CalcShell({ children, driver, onSignOut }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/[0.06] bg-black/60 backdrop-blur sticky top-0 z-20">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/profit-calculator" className="flex items-center gap-3">
            <LogoMark size={28} />
            <Wordmark className="text-base" />
            <span className="text-[10px] uppercase tracking-widest font-black text-[#FF3B30] border border-[#FF3B30]/40 px-2 py-0.5">
              Profit calculator
            </span>
          </Link>
          {driver && (
            <div className="flex items-center gap-4">
              <span className="text-xs text-zinc-400 hidden sm:inline" data-testid="calc-driver-name">
                {driver.name}
                {driver.verified && <ShieldCheck className="w-3.5 h-3.5 text-[#FF3B30] inline ml-1.5 -mt-0.5" />}
              </span>
              <a
                href={mainSiteUrl("/app/driver")}
                className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5"
                data-testid="calc-back-to-marketplace"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Marketplace
              </a>
              <button
                onClick={onSignOut}
                data-testid="calc-signout"
                className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#FF3B30] transition-colors inline-flex items-center gap-1.5"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="px-6 md:px-10 py-12">{children}</main>
    </div>
  );
}

function GateActions() {
  return (
    <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
      <a
        href={mainSiteUrl("/login")}
        data-testid="calc-goto-login"
        className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white px-5 py-3 text-[11px] uppercase tracking-widest font-bold transition-colors"
      >
        Sign in as a driver <ArrowRight className="w-3.5 h-3.5" />
      </a>
      <a
        href={mainSiteUrl("/driver-signup")}
        data-testid="calc-goto-signup"
        className="inline-flex items-center gap-2 border border-white/10 hover:border-white/40 text-zinc-400 hover:text-white px-5 py-3 text-[11px] uppercase tracking-widest font-bold transition-colors"
      >
        Become a driver
      </a>
    </div>
  );
}

function Field({ label, value, onChange, testid, hint, accent }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2" htmlFor={testid}>
        {label}
      </label>
      <input
        id={testid}
        type="number"
        min="0"
        step="any"
        value={value}
        onChange={onChange}
        placeholder="0"
        data-testid={testid}
        className={`w-full bg-[#141416] border ${accent ? "border-[#FF3B30]/50" : "border-white/[0.08]"} focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4 rounded-none transition-colors`}
      />
      {hint && <div className="text-[10px] text-zinc-600 mt-1.5">{hint}</div>}
    </div>
  );
}

function Row({ label, value, testid, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${strong ? "text-white font-bold" : "text-zinc-500"} text-xs uppercase tracking-widest`}>{label}</span>
      <span className={`${strong ? "text-white" : "text-zinc-300"} font-semibold`} data-testid={testid}>{value}</span>
    </div>
  );
}
